'use client';

import { FunctionComponent, PropsWithChildren } from 'react';
import { TargetProvider } from '@tv2/lib-tracking/adobeTarget';
import { ServiceMessagesProvider } from '@/libraries/serviceMessages';
import { useAndroidAppNotification } from '@/hooks/behaviours/useAndroidAppNotification';
import { ExpiredEntity } from '@/components/messages/ExpiredEntity';
import { ProfileOnboarding } from '@/components/profiles/Onboarding';
import { TimeProvider } from '@tv2/ui-time/components/context/TimeContext';
import { UserProvider } from '@tv2/lib-offspring/api/user/UserProvider';
import { getPublicRuntimeEnv } from '@tv2/lib-offspring/libs/publicEnv';
import type { Environment } from '@tv2/lib-offspring/libs/environment';
import { LazyAdminPanel } from '@tv2-cas/offspring/admin/LazyAdminPanel';
import { PathProvider } from '@tv2/lib-offspring/hooks/usePath';
import { AdminProvider } from '@tv2-cas/offspring/admin/AdminProvider';
import { ProfileProvider } from '@/api/user/ProfileProvider';
import QueryClientProviderWrapper from '@/components/admin/QueryClientProviderWrapper';
import { CustomerTypeProvider } from '@/api/subscription/CustomerTypeProvider';
import { SubscriptionProvider } from '@/api/subscription/SubscriptionProvider';
import { QueryCacheProvider } from '@tv2/lib-offspring/api/graphql/QueryCacheProvider';
import { PCSPanels } from '@tv2-cas/offspring/admin/AdminPanel';
import { PageContextProvider } from '@tv2/lib-offspring/components/PageContext';
import { CMPProvider } from '@tv2/ui-cmp/Cookiebot';
import { startCookiebotConsent } from '@tv2/lib-offspring/libs/cookiebotConsent';
import { isConsentCategoryAllowed } from '@tv2/lib-consent/consentPolicy';
import { getConsentDecision } from '@tv2/lib-consent/consentManager';
import { installConsentDebug } from '@tv2/lib-offspring/libs/consentDebug';
import { InformationRoundNext } from '@tv2/ui-system/icons/next/alerts-info/InformationRoundNext';
import { initDatadogLogs } from '@/libraries/initDatadogLogs';
import LeanProfileProviderWrapper from '@/components/profiles/LeanProfileProviderWrapper';
import { ConsentDebugPanel } from '@/components/admin/ConsentDebugPanel';

export type AppEnvironment = Environment | null;

const env = getPublicRuntimeEnv();
const { COOKIE_BOT_URL, TRACKING_ENV } = env;

/**
 * Production ships the read half of `window.playConsent` only. The mutators can grant consent,
 * which would make them a consent bypass reachable by any third-party script on the page.
 */
const CONSENT_DEBUG_MUTATORS = TRACKING_ENV !== 'production';

const GridSetup: FunctionComponent<PropsWithChildren> = ({ children }) => {
  return (
    <div className="layout-12-12-8 576:layout-32-32-8 768:layout-64-64-8 992:layout-64-64-16 1920:layout-96-96-16">
      {children}
    </div>
  );
};

const ADMIN_PANELS = [
  ...PCSPanels,
  {
    panel: <ConsentDebugPanel />,
    buttonData: { icon: InformationRoundNext },
  },
];

initDatadogLogs(false);
if (typeof window !== 'undefined') {
  startCookiebotConsent(COOKIE_BOT_URL);
  installConsentDebug(CONSENT_DEBUG_MUTATORS);

  // Datadog starts without tracking consent and is upgraded once the user allows Statistics.
  // Reading the consent manager rather than Cookiebot directly keeps one vocabulary across the app.
  getConsentDecision().then(() => initDatadogLogs(isConsentCategoryAllowed('Statistics')));
}

/**
 * Reduced provider tree for the `/lite` recovery page.
 */
export function LiteProviders({ children, path }: PropsWithChildren<{ path: string }>) {
  return (
    <PathProvider path={path}>
      <ServiceMessagesProvider updateMinutes={2} path={path}>
        <GridSetup>{children}</GridSetup>
      </ServiceMessagesProvider>
    </PathProvider>
  );
}

/**
 * Full application provider tree. All providers rely on React context, so this
 * must be a Client Component boundary.
 *
 * Hoisted into the `(app)` route-group layout so the tree is mounted **once**
 * and shared across all routes (rather than re-created per route). Because it's
 * a shared ancestor layout, Next.js keeps it mounted across navigations between
 * those routes, keeping a single instance of each client (urql,
 * user/profile/subscription).
 *
 * The per-route page chrome (navigation/footer) is rendered by the `(main)`
 * layout, not here.
 *
 * Per-request data (path, environment) is passed down from the `(app)` Server
 * Component layout as explicit props.
 */
export function MainProviders({
  children,
  path,
  environment,
  disableFallbackRedirect,
}: PropsWithChildren<{
  path?: string;
  environment: AppEnvironment;
  disableFallbackRedirect?: boolean;
}>) {
  useAndroidAppNotification();

  return (
    <CMPProvider scriptUrl={COOKIE_BOT_URL}>
      <QueryCacheProvider>
        <TimeProvider override={environment?.overrideNow} init={environment?.now}>
          <PathProvider path={path}>
            <UserProvider ssrLoggedInHint={environment?.ssrLoggedInHint ?? true}>
              <AdminProvider panels={ADMIN_PANELS}>
                <QueryClientProviderWrapper apiUrl={env.API_URL}>
                  <CustomerTypeProvider>
                    <SubscriptionProvider>
                      <ProfileProvider
                        isParentalControlled={environment?.isParentalControlled ?? false}
                      >
                        <LeanProfileProviderWrapper>
                          <ProfileOnboarding>
                            <TargetProvider>
                              <ServiceMessagesProvider
                                path={path}
                                disableRedirect={disableFallbackRedirect}
                              >
                                <GridSetup>
                                  <LazyAdminPanel />
                                  <PageContextProvider>
                                    <ExpiredEntity path={path} />
                                    {children}
                                  </PageContextProvider>
                                </GridSetup>
                              </ServiceMessagesProvider>
                            </TargetProvider>
                          </ProfileOnboarding>
                        </LeanProfileProviderWrapper>
                      </ProfileProvider>
                    </SubscriptionProvider>
                  </CustomerTypeProvider>
                </QueryClientProviderWrapper>
              </AdminProvider>
            </UserProvider>
          </PathProvider>
        </TimeProvider>
      </QueryCacheProvider>
    </CMPProvider>
  );
}
